import React from "react";
import { HardDrive, Zap, KeyRound, Layers, ScanEye, Usb } from "lucide-react";

const METHODS = [
  {
    icon: Zap,
    drive: "NVMe & SATA SSD",
    method: "Firmware sanitize (block erase)",
    why: "Purges every cell, including spare areas a software overwrite can't reach — the fastest, most thorough method for modern flash.",
  },
  {
    icon: KeyRound,
    drive: "Self-encrypting drive (SED)",
    method: "Cryptographic erase",
    why: "Destroys the encryption key, making all data instantly and permanently unreadable.",
  },
  {
    icon: HardDrive,
    drive: "Hard drive (HDD)",
    method: "Overwrite (1, 3, or 7-pass)",
    why: "Physically replaces the data on the platters, with selectable pass strength for heightened assurance.",
  },
  {
    icon: Layers,
    drive: "Multi-drive PCs (“fusion”)",
    method: "Each drive, its own method",
    why: "Machines with both an SSD and an HDD have every internal drive wiped, each with the correct method.",
  },
];

const PASSES = [
  {
    label: "1-pass",
    title: "Single overwrite",
    body: "A single zero / known-pattern overwrite.",
    standard: "NIST 800-88 Clear",
  },
  {
    label: "3-pass",
    title: "Heightened assurance",
    body: "A multi-pass overwrite for stronger assurance.",
    standard: "DoD 5220.22-M",
  },
  {
    label: "7-pass",
    title: "Maximum assurance",
    body: "Maximum-assurance overwrite for the strictest requirements.",
    standard: "DoD 5220.22-M (ECE)",
  },
];

const ErasureMethods = () => {
  return (
    <section id="methods" className="relative bg-brand-bg font-body">
      <div className="mx-auto max-w-7xl px-6 py-20 lg:py-28">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-line bg-white px-3.5 py-1.5 text-xs font-medium uppercase tracking-[0.16em] text-brand-plum">
            Erasure methods
          </span>
          <h2 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight text-brand-plum sm:text-4xl lg:text-5xl">
            The Right Method for <span className="text-brand-gradient">Every Drive</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink sm:text-lg">
            We always choose the method that is correct for the drive — not a one-size-fits-all approach.
            Each device is detected, classified, and wiped with the method that actually purges it.
          </p>
        </div>

        {/* method cards */}
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {METHODS.map(({ icon: Icon, drive, method, why }) => (
            <div
              key={drive}
              className="flex flex-col rounded-2xl border border-line bg-white p-6 transition-all hover:-translate-y-1 hover:border-brand-orchid hover:shadow-xl hover:shadow-brand-plum/5"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-wash text-brand-magenta ring-1 ring-brand-orchid/30">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-base font-semibold text-brand-plum">{drive}</h3>
              <p className="mt-1 text-sm font-medium text-brand-magenta">{method}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted">{why}</p>
            </div>
          ))}
        </div>

        {/* overwrite strength */}
        <div className="mt-14">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <h3 className="font-display text-xl font-semibold text-brand-plum sm:text-2xl">
              Configurable overwrite strength
            </h3>
            <p className="max-w-md text-sm leading-relaxed text-muted">
              For spinning hard drives, choose the level of assurance your compliance team needs — per device
              or per batch.
            </p>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {PASSES.map(({ label, title, body, standard }) => (
              <div key={label} className="rounded-2xl border border-line bg-white p-5">
                <span className="inline-flex items-center rounded-full bg-brand-plum px-3 py-1 text-xs font-semibold text-white">
                  {label}
                </span>
                <h4 className="mt-3 font-display text-base font-semibold text-brand-plum">{title}</h4>
                <p className="mt-1 text-sm leading-relaxed text-muted">{body}</p>
                <p className="mt-3 text-xs font-medium text-brand-magenta">Follows {standard}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-muted">
            For flash storage (SSD / NVMe), firmware sanitize remains the recommended and most effective
            method, so multi-pass overwrite applies primarily to traditional hard drives.
          </p>
        </div>

        {/* verification + removable-media safety */}
        <div className="mt-10 grid gap-4 md:grid-cols-2">
          <div className="flex items-start gap-3 rounded-2xl border border-brand-orchid/30 bg-brand-wash/60 p-5">
            <ScanEye className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
            <p className="text-sm leading-relaxed text-brand-plum">
              <span className="font-semibold">Read-back verification.</span> After every wipe the system reads
              the drive&apos;s actual bytes directly from the disk to confirm the data is gone.
            </p>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-line bg-white p-5">
            <Usb className="mt-0.5 h-5 w-5 shrink-0 text-brand-magenta" />
            <p className="text-sm leading-relaxed text-brand-plum">
              <span className="font-semibold">Removable media is never touched.</span> Boot media and USB
              devices are automatically protected — only internal drives are wiped.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ErasureMethods;
